import type { GoodsReceipt, GoodsReceiptLine } from "@station/contracts";
import { newIdempotencyKey } from "@station/api-client";
import { Decimal, type LocalDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  cn,
  DataTable,
  type DataTableColumn,
  DateInput,
  Dialog,
  FormField,
  Input,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BadgePercent,
  Ban,
  Check,
  FileDiff,
  Minus,
  PackageCheck,
  Pencil,
  Plus,
  ScanBarcode,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { type ReactElement, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReasonDialog } from "../../../../features/counterparties/shared.js";
import { Section } from "../../../../features/expenses/shared.js";
import { ProductCombobox } from "../../../../features/invoicing/inputs.js";
import { isScannableBarcode, normalizeBarcode } from "../../../../features/market/count-values.js";
import {
  formatStockQuantity,
  isPositiveQuantity,
  moneyExport,
  quantityExport,
} from "../../../../features/market/market-values.js";
import { useGoodsReceipt } from "../../../../features/market/purchasing-queries.js";
import {
  expiryProblemLines,
  goodsReceiptActions,
  isPurchasePrice,
  moveToDamagedBody,
  type ReceiptDifference,
  receiptDifferenceSummary,
  receiptLineBody,
  receiptLineDifference,
  type ReceiptLineFormValues,
  receiptLineFormFrom,
  type ReceiptQuantityField,
  steppedQuantity,
} from "../../../../features/market/purchasing-values.js";
import { useMarketPermissions, useRefreshMarket } from "../../../../features/market/queries.js";
import {
  crumbLink,
  Field,
  GoodsReceiptStatusBadge,
  MarketPage,
  ReceiptCostBadge,
} from "../../../../features/market/shared.js";
import { trTextToApiDecimal } from "../../../../features/pos/pos-values.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

export const Route = createFileRoute("/_app/market/mal-kabul/$id")({ component: GoodsReceiptRoute });

/**
 * MAL KABUL EKRANI — tablet için tam sayfa (Prompt 16 — web, aşama B; ADR 0016 §2, §5).
 *
 * Taslak: büyük barkod alanı (okuyucu klavye gibi yazar ve Enter gönderir) → satır eklenir ya da kabul miktarı
 * artar; "sonraki okutma hasarlı" ile hasarlı miktara yazılır → seçili satır kartında sağlam / hasarlı `+` / `−`,
 * SKT takipli üründe parti no + SKT (ZORUNLU; geçmiş SKT'de "hasarlıya taşı") → satırlar ve fark görünümü → kaydet
 * (sağlam miktar geçici maliyetle stoğa girer; muhasebe fişi faturayla kesilir). Kayıtlı kabulde fark raporu, iptal
 * (ters hareket; faturayla eşleşmişse sunucu 409) ve fiyat önerisi kısayolu. Miktarların kaynağı SUNUCUDUR: her okutma
 * ve düzeltme yanıtı ekrana yazılır (aynı kabulü iki tablet okutabilir). 820 px genişlikte tek sütunda kullanılır.
 */
function GoodsReceiptRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const receipt = useGoodsReceipt(id);
  const title = receipt.data?.receiptNo ?? t("market.receipts.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketGoodsReceipts"), "/market/mal-kabul"), { label: title }]}
    >
      {() =>
        receipt.error ? (
          <Alert tone="danger">{errorMessage(receipt.error)}</Alert>
        ) : !receipt.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <ReceiptScreen receipt={receipt.data} />
        )
      }
    </MarketPage>
  );
}

function ReceiptScreen({ receipt }: { receipt: GoodsReceipt }): ReactElement {
  const { t } = useTranslation();
  const permissions = useMarketPermissions();
  const actions = goodsReceiptActions(receipt);
  return (
    <div className="mx-auto grid w-full max-w-5xl gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{receipt.receiptNo}</span>
        <GoodsReceiptStatusBadge status={receipt.status} />
        {receipt.status === "POSTED" ? <ReceiptCostBadge status={receipt.costStatus} /> : null}
        <span className="text-sm text-muted-foreground">
          {receipt.supplierName} · {receipt.stationName} ·{" "}
          <span className="font-mono">{receipt.warehouseCode}</span>
          {receipt.despatchNo ? ` · ${t("market.receipts.despatchShort", { no: receipt.despatchNo })}` : ""}
        </span>
        {receipt.purchaseOrderId ? (
          <Link
            to="/market/siparisler/$id"
            params={{ id: receipt.purchaseOrderId }}
            className="text-sm underline underline-offset-4"
          >
            {t("market.receipts.orderLink", { no: receipt.purchaseOrderNo })}
          </Link>
        ) : null}
      </div>

      {actions.edit && permissions.canManageReceipts ? (
        <ReceivingPanel receipt={receipt} />
      ) : (
        <PostedReceipt receipt={receipt} />
      )}
    </div>
  );
}

// ── Okutma paneli (taslak) ───────────────────────────────────────────────────
type ScanFeedback = { tone: "success" | "warning" | "danger"; text: string } | null;

function ReceivingPanel({ receipt }: { receipt: GoodsReceipt }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useRefreshMarket();
  const inputRef = useRef<HTMLInputElement>(null);
  const barcodeId = useId();
  const [barcode, setBarcode] = useState("");
  const [damagedNext, setDamagedNext] = useState(false);
  const [feedback, setFeedback] = useState<ScanFeedback>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(receipt.lines[0]?.id ?? null);
  const [adding, setAdding] = useState(false);
  const [editingLine, setEditingLine] = useState<GoodsReceiptLine | null>(null);
  const [posting, setPosting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const selected = receipt.lines.find((line) => line.id === selectedLineId) ?? null;
  const receiptKey = queryKeys.market(receipt.id, "goods-receipt");
  const expiry = expiryProblemLines(receipt.lines);

  /** Okutma yanıtı önbelleğe yazılır (satır + sürüm); toplamlar arkadan tazelenir. */
  const writeScan = (line: GoodsReceiptLine, version: number) => {
    queryClient.setQueryData<GoodsReceipt>(receiptKey, (current) => {
      if (!current) return current;
      const exists = current.lines.some((item) => item.id === line.id);
      const lines = exists
        ? current.lines.map((item) => (item.id === line.id ? line : item))
        : [...current.lines, line];
      return { ...current, lines, lineCount: lines.length, version };
    });
    void queryClient.invalidateQueries({ queryKey: receiptKey });
  };

  const scan = useMutation({
    mutationFn: async (input: { barcode: string; damaged: boolean }) =>
      (
        await api.POST("/market/goods-receipts/{id}/scans", {
          params: { path: { id: receipt.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
          body: { barcode: input.barcode, quantity: "1", damaged: input.damaged } as never,
        })
      ).data!,
    onSuccess: (result, input) => {
      writeScan(result.line, result.receiptVersion);
      setSelectedLineId(result.line.id);
      setFeedback({
        tone: "success",
        text: t(input.damaged ? "market.receipts.scannedDamaged" : "market.receipts.scanned", {
          product: result.line.productName,
          received: formatStockQuantity(result.line.receivedQuantity),
          damaged: formatStockQuantity(result.line.damagedQuantity),
        }),
      });
      if (result.line.expiryWarning === "REQUIRED")
        setFeedback({
          tone: "warning",
          text: t("market.receipts.scannedNeedsExpiry", { product: result.line.productName }),
        });
    },
    onError: (caught) =>
      // Bilinmeyen barkod SAKİN bir uyarıdır (ürün katalogda yok): kırmızı hata değil, ne yapılacağı yazılır.
      setFeedback(
        isApiError(caught, "UNKNOWN_BARCODE")
          ? { tone: "warning", text: `${errorMessage(caught)} ${t("market.receipts.unknownBarcodeHint")}` }
          : { tone: "danger", text: errorMessage(caught) },
      ),
    onSettled: () => inputRef.current?.focus(),
  });

  /** Satır düzeltmesi (±, SKT, maliyet): tam kabul döner ve önbelleğe yazılır. */
  const updateLine = useMutation({
    mutationFn: async (input: { lineId: string; body: Record<string, unknown> }) =>
      (
        await api.PUT("/market/goods-receipts/{id}/lines/{lineId}", {
          params: { path: { id: receipt.id, lineId: input.lineId } },
          body: { ...input.body, version: receipt.version } as never,
        })
      ).data!,
    onSuccess: (saved) => {
      queryClient.setQueryData(receiptKey, saved);
      setEditingLine(null);
    },
    onError: async (caught) => {
      setFeedback({ tone: "danger", text: errorMessage(caught) });
      // Sürüm çakışması (başka tablet okuttu): güncel hâl yüklenir, kullanıcı yeniden dener.
      if (isApiError(caught, "VERSION_CONFLICT"))
        await queryClient.invalidateQueries({ queryKey: receiptKey });
    },
  });

  const cancel = async (reason: string) => {
    const fresh = (await api.GET("/market/goods-receipts/{id}", { params: { path: { id: receipt.id } } }))
      .data!;
    await api.POST("/market/goods-receipts/{id}/cancel", {
      params: { path: { id: receipt.id } },
      body: { version: fresh.version, reason },
    });
    await refresh();
    toast({ tone: "success", title: t("market.receipts.done.cancelDraft") });
  };

  const submitScan = () => {
    const code = normalizeBarcode(barcode);
    setBarcode("");
    if (!isScannableBarcode(code)) {
      setFeedback({ tone: "danger", text: t("market.counts.barcodeTooShort") });
      inputRef.current?.focus();
      return;
    }
    scan.mutate({ barcode: code, damaged: damagedNext });
    // "Sonraki okutma hasarlı" TEK SEFERLİKTİR: yanlışlıkla sağlam malın hasarlı yazılması önlenir.
    setDamagedNext(false);
  };

  return (
    <>
      <Card className="grid gap-4 p-4 sm:p-5">
        <form
          className="grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submitScan();
          }}
        >
          <label htmlFor={barcodeId} className="text-base font-semibold">
            {t("market.counts.barcodeLabel")}
          </label>
          <div className="flex flex-wrap gap-2">
            <Input
              ref={inputRef}
              id={barcodeId}
              autoFocus
              autoComplete="off"
              inputMode="numeric"
              enterKeyHint="go"
              className="h-14 min-w-0 flex-1 font-mono text-2xl"
              value={barcode}
              aria-describedby={`${barcodeId}-hint`}
              onChange={(event) => setBarcode(event.target.value)}
            />
            <Button type="submit" size="lg" className="h-14 px-6" loading={scan.isPending}>
              <ScanBarcode aria-hidden="true" />
              {t("market.counts.scan")}
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p id={`${barcodeId}-hint`} className="text-sm text-muted-foreground">
              {t("market.receipts.barcodeHint")}
            </p>
            <Button
              type="button"
              variant={damagedNext ? "destructive" : "outline"}
              aria-pressed={damagedNext}
              onClick={() => {
                setDamagedNext((current) => !current);
                inputRef.current?.focus();
              }}
            >
              <TriangleAlert aria-hidden="true" />
              {t("market.receipts.damagedNext")}
            </Button>
          </div>
        </form>
        <div aria-live="polite" role="status" data-testid="scan-feedback">
          {feedback ? (
            <p
              className={cn(
                "rounded-md px-3 py-2 text-base font-medium",
                feedback.tone === "success" && "bg-success-subtle text-success",
                feedback.tone === "warning" && "bg-warning-subtle text-warning",
                feedback.tone === "danger" && "bg-danger-subtle text-destructive",
              )}
            >
              {feedback.text}
            </p>
          ) : null}
        </div>
      </Card>

      {selected ? (
        <SelectedLineCard
          key={selected.id}
          line={selected}
          fromOrder={receipt.purchaseOrderId !== null}
          busy={scan.isPending || updateLine.isPending}
          onStep={(field, delta) => {
            const next = steppedQuantity(selected[field], delta);
            if (next !== null) updateLine.mutate({ lineId: selected.id, body: { [field]: next } });
          }}
          onExpiry={(lotNo, expiryDate) =>
            updateLine.mutate({ lineId: selected.id, body: { lotNo, expiryDate } })
          }
          onMoveToDamaged={() =>
            updateLine.mutate({ lineId: selected.id, body: moveToDamagedBody(selected) })
          }
          onEdit={() => setEditingLine(selected)}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t("market.receipts.noSelection")}</p>
      )}

      <ReceiptLineList
        receipt={receipt}
        selectedLineId={selectedLineId}
        onSelect={setSelectedLineId}
        onAdd={() => setAdding(true)}
      />

      <DifferenceSummary receipt={receipt} />

      {expiry.required.length + expiry.past.length > 0 ? (
        <Alert tone="warning" title={t("market.receipts.expiryBlockTitle")}>
          {[
            expiry.required.length > 0
              ? t("market.receipts.expiryBlockRequired", { count: expiry.required.length })
              : null,
            expiry.past.length > 0
              ? t("market.receipts.expiryBlockPast", { count: expiry.past.length })
              : null,
            t("market.receipts.expiryBlockFix"),
          ]
            .filter(Boolean)
            .join(" ")}
        </Alert>
      ) : null}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" size="lg" onClick={() => setCancelling(true)}>
          <Ban aria-hidden="true" />
          {t("market.receipts.cancelDraft")}
        </Button>
        <Button size="lg" disabled={receipt.lines.length === 0} onClick={() => setPosting(true)}>
          <PackageCheck aria-hidden="true" />
          {t("market.receipts.post")}
        </Button>
      </div>

      <AddLineDialog open={adding} onOpenChange={setAdding} receipt={receipt} onAdded={setSelectedLineId} />
      {editingLine ? (
        <LineEditDialog
          line={editingLine}
          busy={updateLine.isPending}
          receipt={receipt}
          onClose={() => setEditingLine(null)}
          onSave={(body) => updateLine.mutate({ lineId: editingLine.id, body })}
        />
      ) : null}
      <PostDialog open={posting} onOpenChange={setPosting} receipt={receipt} />
      <ReasonDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("market.receipts.cancelDraftTitle")}
        description={t("market.receipts.cancelDraftDescription")}
        confirmLabel={t("market.receipts.cancelDraft")}
        onConfirm={cancel}
      />
    </>
  );
}

function SelectedLineCard({
  line,
  fromOrder,
  busy,
  onStep,
  onExpiry,
  onMoveToDamaged,
  onEdit,
}: {
  line: GoodsReceiptLine;
  fromOrder: boolean;
  busy: boolean;
  onStep: (field: ReceiptQuantityField, delta: 1 | -1) => void;
  onExpiry: (lotNo: string | null, expiryDate: string | null) => void;
  onMoveToDamaged: () => void;
  onEdit: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [lotNo, setLotNo] = useState(line.lotNo ?? "");
  const [expiryDate, setExpiryDate] = useState<LocalDate | null>(line.expiryDate as LocalDate | null);
  const expiryId = useId();
  const difference = receiptLineDifference(line);

  return (
    <Card
      className="grid gap-4 p-4 sm:p-5"
      aria-label={t("market.receipts.selectedCard")}
      data-testid="receipt-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <h2 className="text-xl font-semibold">{line.productName}</h2>
          <p className="font-mono text-sm text-muted-foreground">
            {line.productCode}
            {line.expectedQuantity !== null
              ? ` · ${t("market.receipts.expected", { quantity: formatStockQuantity(line.expectedQuantity) })}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DifferenceBadge difference={difference} line={line} fromOrder={fromOrder} />
          <Button variant="outline" onClick={onEdit}>
            <Pencil aria-hidden="true" />
            {t("market.receipts.editLine")}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityStepper
          label={t("market.receipts.fields.received")}
          testId="received-quantity"
          productName={line.productName}
          field="receivedQuantity"
          value={line.receivedQuantity}
          busy={busy}
          onStep={onStep}
        />
        <QuantityStepper
          label={t("market.receipts.fields.damaged")}
          testId="damaged-quantity"
          productName={line.productName}
          field="damagedQuantity"
          value={line.damagedQuantity}
          busy={busy}
          onStep={onStep}
        />
      </div>

      {line.trackExpiry ? (
        <form
          className="grid gap-2 rounded-md border p-3"
          noValidate
          aria-describedby={`${expiryId}-hint`}
          onSubmit={(event) => {
            event.preventDefault();
            onExpiry(lotNo.trim() === "" ? null : lotNo.trim(), expiryDate);
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <FormField label={t("market.fields.lotNo")} className="w-40">
              {(control) => (
                <Input
                  {...control}
                  maxLength={60}
                  autoComplete="off"
                  value={lotNo}
                  onChange={(event) => setLotNo(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("market.fields.expiryDate")} required className="w-44">
              {(control) => (
                <DateInput
                  {...control}
                  aria-invalid={line.expiryWarning !== null}
                  value={expiryDate}
                  onValueChange={setExpiryDate}
                />
              )}
            </FormField>
            <Button type="submit" variant="outline" disabled={busy}>
              <Check aria-hidden="true" />
              {t("market.receipts.saveExpiry")}
            </Button>
          </div>
          <p id={`${expiryId}-hint`} className="text-xs text-muted-foreground">
            {t("market.receipts.expiryHint")}
          </p>
          {line.expiryWarning === "REQUIRED" ? (
            <p className="text-sm font-medium text-warning">{t("market.receipts.expiryRequired")}</p>
          ) : null}
          {line.expiryWarning === "PAST" ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-destructive">{t("market.receipts.expiryPast")}</p>
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onMoveToDamaged}>
                <TriangleAlert aria-hidden="true" />
                {t("market.receipts.moveToDamaged")}
              </Button>
            </div>
          ) : null}
        </form>
      ) : null}
    </Card>
  );
}

function QuantityStepper({
  label,
  testId,
  productName,
  field,
  value,
  busy,
  onStep,
}: {
  label: string;
  testId: string;
  productName: string;
  field: ReceiptQuantityField;
  value: string;
  busy: boolean;
  onStep: (field: ReceiptQuantityField, delta: 1 | -1) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="outline"
        className="size-14 [&_svg]:size-7"
        aria-label={t("market.receipts.decrement", { product: productName, field: label })}
        disabled={busy || steppedQuantity(value, -1) === null}
        onClick={() => onStep(field, -1)}
      >
        <Minus aria-hidden="true" />
      </Button>
      <div className="grid min-w-24 justify-items-center">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-3xl font-semibold tabular" data-testid={testId}>
          {formatStockQuantity(value)}
        </span>
      </div>
      <Button
        className="size-14 [&_svg]:size-7"
        aria-label={t("market.receipts.increment", { product: productName, field: label })}
        disabled={busy}
        onClick={() => onStep(field, 1)}
      >
        <Plus aria-hidden="true" />
      </Button>
    </div>
  );
}

function DifferenceBadge({
  difference,
  line,
  fromOrder,
}: {
  difference: ReceiptDifference;
  line: Pick<GoodsReceiptLine, "differenceQuantity">;
  /** Serbest (siparişsiz) kabulde beklenen yoktur: "siparişte yok" rozeti anlamsızdır, çizilmez. */
  fromOrder: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!fromOrder) return null;
  if (difference === "NONE")
    return (
      <StatusBadge tone="success" className="whitespace-nowrap">
        {t("market.receipts.difference.NONE")}
      </StatusBadge>
    );
  if (difference === "UNEXPECTED")
    return (
      <StatusBadge tone="info" className="whitespace-nowrap">
        {t("market.receipts.difference.UNEXPECTED")}
      </StatusBadge>
    );
  const quantity = formatStockQuantity(line.differenceQuantity?.replace("-", "") ?? "0");
  return (
    <StatusBadge tone={difference === "SHORT" ? "warning" : "info"} className="whitespace-nowrap">
      {t(`market.receipts.difference.${difference}`, { quantity })}
    </StatusBadge>
  );
}

function ReceiptLineList({
  receipt,
  selectedLineId,
  onSelect,
  onAdd,
}: {
  receipt: GoodsReceipt;
  selectedLineId: string | null;
  onSelect: (lineId: string) => void;
  onAdd: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Section
      title={t("market.receipts.linesTitle")}
      actions={
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus aria-hidden="true" />
          {t("market.receipts.addLine")}
        </Button>
      }
    >
      {receipt.lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("market.receipts.noLines")}</p>
      ) : (
        <ul className="grid gap-1.5" aria-label={t("market.receipts.linesTitle")}>
          {receipt.lines.map((line) => (
            <li key={line.id}>
              <button
                type="button"
                aria-pressed={line.id === selectedLineId}
                onClick={() => onSelect(line.id)}
                className={cn(
                  "flex min-h-12 w-full flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted",
                  line.id === selectedLineId && "border-primary bg-primary/5",
                )}
              >
                <span className="grid gap-0.5">
                  <span className="font-medium">{line.productName}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {line.productCode}
                    {line.lotNo ? ` · ${line.lotNo}` : ""}
                    {line.expiryDate ? ` · SKT ${formatDate(line.expiryDate)}` : ""}
                  </span>
                </span>
                <span className="flex flex-wrap items-center justify-end gap-2">
                  {line.expiryWarning ? (
                    <StatusBadge tone={line.expiryWarning === "PAST" ? "danger" : "warning"}>
                      {t(`market.receipts.expiryWarning.${line.expiryWarning}`)}
                    </StatusBadge>
                  ) : null}
                  <DifferenceBadge
                    difference={receiptLineDifference(line)}
                    line={line}
                    fromOrder={receipt.purchaseOrderId !== null}
                  />
                  <span className="grid justify-items-end text-xs text-muted-foreground">
                    <span>
                      {t("market.receipts.lineQuantities", {
                        received: formatStockQuantity(line.receivedQuantity),
                        damaged: formatStockQuantity(line.damagedQuantity),
                      })}
                    </span>
                    {line.expectedQuantity !== null ? (
                      <span>
                        {t("market.receipts.expected", {
                          quantity: formatStockQuantity(line.expectedQuantity),
                        })}
                      </span>
                    ) : null}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Fark özeti: beklenen / kabul / hasarlı / eksik / fazla (satırlardan yeniden toplanır, başlıkla karşılaştırılır). */
function DifferenceSummary({ receipt }: { receipt: GoodsReceipt }): ReactElement {
  const { t } = useTranslation();
  const summary = receiptDifferenceSummary(receipt);
  return (
    <Section title={t("market.receipts.differenceTitle")}>
      <dl className="grid gap-3 sm:grid-cols-5" data-testid="receipt-difference">
        <Field label={t("market.receipts.fields.expected")}>
          {receipt.purchaseOrderId ? formatStockQuantity(summary.expected) : "—"}
        </Field>
        <Field label={t("market.receipts.fields.received")}>{formatStockQuantity(summary.received)}</Field>
        <Field label={t("market.receipts.fields.damaged")}>{formatStockQuantity(summary.damaged)}</Field>
        {/* Serbest kabulde beklenen yoktur: eksik / fazla anlamsızdır (sunucu siparişsiz satırı "fazla" sayar). */}
        <Field label={t("market.receipts.fields.short")}>
          {receipt.purchaseOrderId ? formatStockQuantity(summary.short) : "—"}
        </Field>
        <Field label={t("market.receipts.fields.over")}>
          {receipt.purchaseOrderId ? formatStockQuantity(summary.over) : "—"}
        </Field>
      </dl>
      {summary.unexpectedLines > 0 && receipt.purchaseOrderId ? (
        <p className="text-sm text-muted-foreground">
          {t("market.receipts.unexpectedLines", { count: summary.unexpectedLines })}
        </p>
      ) : null}
      {summary.consistent ? null : <Alert tone="warning">{t("market.receipts.inconsistent")}</Alert>}
    </Section>
  );
}

function AddLineDialog({
  open,
  onOpenChange,
  receipt,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receipt: GoodsReceipt;
  onAdded: (lineId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const productFieldId = useId();
  const [product, setProduct] = useState<{ id: string; label: string } | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [error, setError] = useState<string | null>(null);
  const quantityApi = trTextToApiDecimal(quantity);
  const costApi = trTextToApiDecimal(unitCost);
  const valid =
    product !== null &&
    isPositiveQuantity(quantityApi) &&
    (unitCost.trim() === "" || isPurchasePrice(costApi));

  const add = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/market/goods-receipts/{id}/lines", {
          params: { path: { id: receipt.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
          body: {
            productId: product?.id ?? "",
            receivedQuantity: quantityApi,
            unitCost: unitCost.trim() === "" ? null : costApi,
            version: receipt.version,
          } as never,
        })
      ).data!,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.market(receipt.id, "goods-receipt"), saved);
      const added = saved.lines.find((line) => line.productId === product?.id);
      if (added) onAdded(added.id);
      setProduct(null);
      setQuantity("1");
      setUnitCost("");
      onOpenChange(false);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("market.receipts.addLineTitle")}
      description={t("market.receipts.addLineDescription")}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          if (valid) add.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-1.5">
          <label htmlFor={productFieldId} className="text-sm font-medium">
            {t("market.fields.product")}
          </label>
          <ProductCombobox
            id={productFieldId}
            label={product?.label ?? ""}
            onChange={(option) =>
              setProduct(option ? { id: option.id, label: `${option.code} ${option.name}` } : null)
            }
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t("market.receipts.fields.received")} required>
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            )}
          </FormField>
          <FormField
            label={t("market.receipts.fields.unitCost")}
            description={t("market.receipts.unitCostHint")}
          >
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={unitCost}
                onChange={(event) => setUnitCost(event.target.value)}
              />
            )}
          </FormField>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={add.isPending} disabled={!valid}>
            {t("market.receipts.addLine")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function LineEditDialog({
  line,
  busy,
  receipt,
  onClose,
  onSave,
}: {
  line: GoodsReceiptLine;
  busy: boolean;
  receipt: GoodsReceipt;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ReceiptLineFormValues>(() => receiptLineFormFrom(line));
  const [showProblems, setShowProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { body, problems } = receiptLineBody(values, line.trackExpiry);
  const patch = (next: Partial<ReceiptLineFormValues>) => setValues((current) => ({ ...current, ...next }));
  const problem = (field: "receivedQuantity" | "damagedQuantity" | "unitCost") =>
    showProblems && problems.includes(field) ? t(`market.receipts.lineProblem.${field}`) : undefined;

  const remove = useMutation({
    mutationFn: async () =>
      (
        await api.DELETE("/market/goods-receipts/{id}/lines/{lineId}", {
          params: { path: { id: receipt.id, lineId: line.id } },
        })
      ).data!,
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.market(receipt.id, "goods-receipt"), saved);
      onClose();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("market.receipts.editLineTitle", { product: line.productName })}
      description={t("market.receipts.editLineDescription")}
      className="sm:max-w-2xl"
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setShowProblems(true);
          if (problems.length === 0) onSave(body);
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label={t("market.receipts.fields.received")} error={problem("receivedQuantity")}>
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={values.receivedQuantity}
                onChange={(event) => patch({ receivedQuantity: event.target.value })}
              />
            )}
          </FormField>
          <FormField label={t("market.receipts.fields.damaged")} error={problem("damagedQuantity")}>
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={values.damagedQuantity}
                onChange={(event) => patch({ damagedQuantity: event.target.value })}
              />
            )}
          </FormField>
          <FormField
            label={t("market.receipts.fields.unitCost")}
            error={problem("unitCost")}
            description={t("market.receipts.provisionalHint")}
          >
            {(control) => (
              <Input
                {...control}
                inputMode="decimal"
                className="text-right tabular"
                value={values.unitCost}
                onChange={(event) => patch({ unitCost: event.target.value })}
              />
            )}
          </FormField>
          {line.trackExpiry ? (
            <>
              <FormField label={t("market.fields.lotNo")}>
                {(control) => (
                  <Input
                    {...control}
                    maxLength={60}
                    value={values.lotNo}
                    onChange={(event) => patch({ lotNo: event.target.value })}
                  />
                )}
              </FormField>
              <FormField label={t("market.fields.expiryDate")} required>
                {(control) => (
                  <DateInput
                    {...control}
                    value={values.expiryDate as LocalDate | null}
                    onValueChange={(next) => patch({ expiryDate: next })}
                  />
                )}
              </FormField>
            </>
          ) : null}
        </div>
        <FormField label={t("market.fields.note")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={300}
              value={values.note}
              onChange={(event) => patch({ note: event.target.value })}
            />
          )}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button variant="outline" loading={remove.isPending} onClick={() => remove.mutate()}>
            <Trash2 aria-hidden="true" />
            {t("market.receipts.removeLine")}
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

/** Kaydet onayı: stoğa girer, fiş faturayla kesilir. SKT hatası (422) pencerede okunur ve çözüm ipucu verilir. */
function PostDialog({
  open,
  onOpenChange,
  receipt,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receipt: GoodsReceipt;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const [error, setError] = useState<{ text: string; lotInvalid: boolean } | null>(null);
  const summary = receiptDifferenceSummary(receipt);

  const post = useMutation({
    mutationFn: async () => {
      // Okutmalar sürümü ilerletmiş olabilir: kayıt GÜNCEL sürümle yapılır.
      const fresh = (await api.GET("/market/goods-receipts/{id}", { params: { path: { id: receipt.id } } }))
        .data!;
      return (
        await api.POST("/market/goods-receipts/{id}/post", {
          params: { path: { id: receipt.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
          body: { version: fresh.version },
        })
      ).data!;
    },
    onSuccess: async () => {
      onOpenChange(false);
      await refresh();
      toast({ tone: "success", title: t("market.receipts.done.post") });
    },
    onError: (caught) =>
      setError({
        text: errorMessage(caught),
        lotInvalid: isApiError(caught, "GOODS_RECEIPT_LOT_INVALID"),
      }),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!post.isPending) {
          setError(null);
          onOpenChange(next);
        }
      }}
      title={t("market.receipts.postTitle")}
      description={t("market.receipts.postDescription")}
      dismissible={!post.isPending}
      footer={
        <>
          <Button variant="outline" disabled={post.isPending} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button loading={post.isPending} onClick={() => post.mutate()}>
            <PackageCheck aria-hidden="true" />
            {t("market.receipts.post")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        {error ? (
          <Alert tone="danger" title={error.lotInvalid ? t("market.receipts.lotInvalidTitle") : undefined}>
            <p>{error.text}</p>
            {error.lotInvalid ? <p className="mt-1">{t("market.receipts.lotInvalidHint")}</p> : null}
          </Alert>
        ) : null}
        <dl className="grid gap-3 sm:grid-cols-3">
          <Field label={t("market.receipts.fields.received")}>{formatStockQuantity(summary.received)}</Field>
          <Field label={t("market.receipts.fields.damaged")}>{formatStockQuantity(summary.damaged)}</Field>
          <Field label={t("market.receipts.fields.netAmount")}>
            <MoneyText value={receipt.netAmount} />
          </Field>
        </dl>
        {receipt.purchaseOrderId && !Decimal.from(summary.short).isZero() ? (
          <p className="text-sm text-muted-foreground">
            {t("market.receipts.postShortHint", { quantity: formatStockQuantity(summary.short) })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ── Kayıtlı / iptal edilmiş kabul ────────────────────────────────────────────
function PostedReceipt({ receipt }: { receipt: GoodsReceipt }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const actions = goodsReceiptActions(receipt);
  const [cancelling, setCancelling] = useState(false);

  const cancel = async (reason: string) => {
    await api.POST("/market/goods-receipts/{id}/cancel", {
      params: { path: { id: receipt.id } },
      body: { version: receipt.version, reason },
    });
    await refresh();
    toast({ tone: "success", title: t("market.receipts.done.cancel") });
  };

  const columns: DataTableColumn<GoodsReceiptLine>[] = [
    {
      id: "product",
      header: t("market.fields.product"),
      hideable: false,
      exportValue: (row) => `${row.productCode} ${row.productName}`,
      cell: (row) => (
        <span className="grid gap-0.5">
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: receipt.stationId }}
            className="underline underline-offset-4"
          >
            {row.productName}
          </Link>
          <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
        </span>
      ),
    },
    {
      id: "lot",
      header: t("market.fields.lotNo"),
      exportValue: (row) => [row.lotNo, row.expiryDate].filter(Boolean).join(" · ") || null,
      cell: (row) =>
        row.lotNo === null && row.expiryDate === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="grid gap-0.5">
            <span className="font-mono text-xs">{row.lotNo ?? t("market.lots.noLotNo")}</span>
            <span className="text-xs text-muted-foreground">SKT {formatDate(row.expiryDate)}</span>
          </span>
        ),
    },
    {
      id: "expected",
      header: t("market.receipts.fields.expected"),
      align: "right",
      exportValue: (row) => quantityExport(row.expectedQuantity),
      cell: (row) => <span className="tabular">{formatStockQuantity(row.expectedQuantity)}</span>,
    },
    {
      id: "received",
      header: t("market.receipts.fields.received"),
      align: "right",
      exportValue: (row) => quantityExport(row.receivedQuantity),
      cell: (row) => <span className="tabular">{formatStockQuantity(row.receivedQuantity, row.unit)}</span>,
    },
    {
      id: "damaged",
      header: t("market.receipts.fields.damaged"),
      align: "right",
      exportValue: (row) => quantityExport(row.damagedQuantity),
      cell: (row) => <span className="tabular">{formatStockQuantity(row.damagedQuantity)}</span>,
    },
    {
      id: "difference",
      header: t("market.receipts.fields.difference"),
      align: "right",
      exportValue: (row) => quantityExport(row.differenceQuantity),
      // Rozet farkı METİNLE yazar ("Eksik 24", "Fazla 6", "Tam", "Siparişte yok"); sayı Excel'e ayrıca gider.
      cell: (row) =>
        receipt.purchaseOrderId === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <DifferenceBadge difference={receiptLineDifference(row)} line={row} fromOrder />
        ),
    },
    {
      id: "unitCost",
      header: t("market.receipts.fields.unitCost"),
      align: "right",
      exportValue: (row) => row.unitCost,
      cell: (row) => <span className="tabular whitespace-nowrap">{formatUnitPrice(row.unitCost)}</span>,
    },
    {
      id: "finalUnitCost",
      header: t("market.receipts.fields.finalUnitCost"),
      align: "right",
      exportValue: (row) => row.finalUnitCost,
      cell: (row) => <span className="tabular whitespace-nowrap">{formatUnitPrice(row.finalUnitCost)}</span>,
    },
    {
      id: "lineAmount",
      header: t("market.receipts.fields.lineAmount"),
      align: "right",
      exportValue: (row) => moneyExport(row.lineAmount),
      cell: (row) => <MoneyText value={row.lineAmount} />,
    },
  ];

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions.priceSuggestion ? (
          <Button variant="outline" asChild>
            <Link to="/market/fiyat-onerisi" search={{ station: receipt.stationId, receipt: receipt.id }}>
              <BadgePercent aria-hidden="true" />
              {t("market.receipts.priceSuggestion")}
            </Link>
          </Button>
        ) : null}
        {receipt.matchId ? (
          <Button variant="outline" asChild>
            <Link to="/market/eslestirme/$id" params={{ id: receipt.matchId }}>
              <FileDiff aria-hidden="true" />
              {t("market.receipts.openMatch")}
            </Link>
          </Button>
        ) : null}
        {actions.cancel && permissions.canManageReceipts ? (
          <Button variant="destructive" className="ms-auto" onClick={() => setCancelling(true)}>
            <Ban aria-hidden="true" />
            {t("market.receipts.cancel")}
          </Button>
        ) : null}
      </div>

      {receipt.status === "CANCELLED" ? (
        <Alert tone="warning" title={t("market.receipts.cancelledTitle")}>
          {receipt.cancelledReason ?? "—"}
        </Alert>
      ) : null}
      {receipt.status === "POSTED" && receipt.matchId !== null ? (
        <Alert tone="info">{t("market.receipts.matchedHint")}</Alert>
      ) : null}
      {receipt.status === "POSTED" && receipt.costStatus === "PROVISIONAL" ? (
        <Alert tone="info">{t("market.receipts.provisionalNotice")}</Alert>
      ) : null}

      <Section title={t("market.receipts.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.orders.fields.supplier")}>{receipt.supplierName}</Field>
          <Field label={t("market.fields.warehouse")}>
            {receipt.stationName} · <span className="font-mono">{receipt.warehouseCode}</span>
          </Field>
          <Field label={t("market.receipts.fields.despatchNo")}>
            {receipt.despatchNo ?? "—"} · {formatDate(receipt.despatchDate)}
          </Field>
          <Field label={t("market.receipts.fields.businessDate")}>{formatDate(receipt.businessDate)}</Field>
          <Field label={t("market.receipts.fields.netAmount")}>
            <MoneyText value={receipt.netAmount} />
          </Field>
          <Field label={t("market.receipts.fields.postedAt")}>{formatDateTime(receipt.postedAt)}</Field>
          <Field label={t("market.receipts.fields.cost")}>
            <ReceiptCostBadge status={receipt.costStatus} />
          </Field>
          <Field label={t("market.fields.createdAt")}>{formatDateTime(receipt.createdAt)}</Field>
        </dl>
        {receipt.note ? <p className="text-sm">{receipt.note}</p> : null}
      </Section>

      <DifferenceSummary receipt={receipt} />

      <DataTable
        caption={t("market.receipts.reportCaption")}
        columns={columns}
        rows={receipt.lines}
        rowKey={(row) => row.id}
        emptyState={<p className="p-4 text-sm text-muted-foreground">{t("market.receipts.noLines")}</p>}
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `mal-kabul-${receipt.receiptNo}`,
            sheetName: t("market.receipts.reportCaption"),
            columns: visible,
            rows: receipt.lines,
          });
        }}
      />

      <ReasonDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("market.receipts.cancelTitle")}
        description={t("market.receipts.cancelDescription")}
        confirmLabel={t("market.receipts.cancel")}
        onConfirm={cancel}
      />
    </>
  );
}
